
import React from 'react';
import SectionLayout from '@/components/SectionLayout';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Crown, Users, TrendingUp, Eye, Shield, Star, X, CheckCircle2, AlertTriangle, Trophy } from 'lucide-react';

const ProfessionalPositioningSection = () => {
  const { isMobile } = useBreakpoint();

  const amateurTraits = [
    {
      icon: X,
      title: "Generic Templates",
      description: "Using free templates that scream 'amateur hour'",
      impact: "Clients question your credibility"
    },
    {
      icon: AlertTriangle,
      title: "Inconsistent Branding",
      description: "Mixed fonts, colors, and messaging across materials",
      impact: "Looks unprofessional and thrown together"
    },
    {
      icon: X,
      title: "DIY Sales Materials",
      description: "Cobbled together presentations and proposals",
      impact: "Clients assume you're inexperienced"
    }
  ];

  const professionalTraits = [
    {
      icon: CheckCircle2,
      title: "Premium Design System",
      description: "Cohesive, professionally designed materials",
      impact: "Instant credibility and trust"
    },
    {
      icon: Crown,
      title: "Consistent Brand Identity",
      description: "Every touchpoint reinforces your expertise",
      impact: "Clients see you as the premium choice"
    },
    {
      icon: Trophy,
      title: "Polished Presentations",
      description: "Sales materials that wow and convert",
      impact: "Higher prices, better clients"
    }
  ];

  const positioningBenefits = [
    {
      icon: TrendingUp,
      title: "Command Premium Prices",
      description: "Professional positioning justifies higher rates"
    },
    {
      icon: Users,
      title: "Attract Better Clients", 
      description: "Quality clients gravitate to professional service providers"
    },
    {
      icon: Shield,
      title: "Build Instant Trust",
      description: "Polished materials create immediate confidence"
    },
    {
      icon: Eye,
      title: "Stand Out from Competition",
      description: "Most competitors use amateur-looking materials"
    }
  ];

  return (
    <SectionLayout 
      backgroundColor="bg-gradient-to-br from-white via-[#FEE6D8]/5 to-white" 
      paddingY="py-16 md:py-20 lg:py-24"
      containerWidth="wide"
      backgroundPattern="dots"
      patternColor="#003a70"
      patternOpacity={2}
    >
      <div className="text-center space-y-12 md:space-y-16">
        
        {/* Section Header */}
        <div className="space-y-6 animate-fade-in">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#003a70] to-[#003a70]/90 rounded-full shadow-lg">
            <Crown className="text-white" size={isMobile ? 24 : 28} />
            <span className="text-white font-bold text-lg md:text-xl">Professional Positioning</span>
          </div>
          
          <h2 className={cn(
            "font-bold text-[#003a70] leading-tight max-w-4xl mx-auto",
            isMobile ? "text-2xl md:text-3xl" : "text-3xl lg:text-4xl xl:text-5xl"
          )}>
            It's About 
            <span className="text-[#FEE6D8] bg-gradient-to-r from-[#003a70] to-[#003a70]/80 bg-clip-text text-transparent">
              {" "}Professional Positioning
            </span>
          </h2>
          
          <p className={cn(
            "text-[#003a70]/80 leading-relaxed max-w-3xl mx-auto",
            isMobile ? "text-lg" : "text-xl lg:text-2xl"
          )}>
            Your materials either position you as an amateur or an expert. 
            There's no middle ground.
          </p>
        </div>

        {/* Amateur vs Professional Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-7xl mx-auto">
          
          {/* Amateur Side */}
          <div className="relative bg-gradient-to-br from-red-50 to-red-100/50 rounded-3xl p-8 md:p-10 border-2 border-red-200/50 shadow-xl animate-fade-in" style={{ animationDelay: '0.1s' }}>
            {/* Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <div className="bg-red-500 text-white px-6 py-2 rounded-full shadow-lg flex items-center gap-2">
                <X size={20} />
                <span className="font-bold text-sm">AMATEUR APPROACH</span>
              </div>
            </div>
            
            <div className="mt-8 space-y-8">
              {/* Header */}
              <div className="text-center">
                <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <AlertTriangle className="text-white" size={32} />
                </div>
                <h3 className={cn(
                  "font-bold text-red-700 mb-2",
                  isMobile ? "text-2xl" : "text-3xl"
                )}>
                  How Most People Look
                </h3>
                <p className="text-red-600 font-medium">
                  Generic, unprofessional, forgettable
                </p>
              </div>
              
              {/* Traits List */}
              <div className="space-y-4">
                {amateurTraits.map((trait, index) => (
                  <div 
                    key={index} 
                    className="bg-white/60 rounded-xl p-6 border border-red-200/50"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <trait.icon className="text-white" size={16} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-red-700 mb-2 text-lg">
                          {trait.title}
                        </h4>
                        <p className="text-red-600 text-sm md:text-base mb-3">
                          {trait.description}
                        </p>
                        <div className="bg-red-100 rounded-lg p-3 border-l-4 border-red-400">
                          <p className="text-red-700 font-medium text-sm">
                            Result: {trait.impact}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Professional Side */}
          <div className="relative bg-gradient-to-br from-green-50 to-[#FEE6D8]/30 rounded-3xl p-8 md:p-10 border-2 border-[#FEE6D8]/50 shadow-xl animate-fade-in" style={{ animationDelay: '0.2s' }}>
            {/* Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <div className="bg-gradient-to-r from-[#003a70] to-[#003a70]/90 text-white px-6 py-2 rounded-full shadow-lg flex items-center gap-2">
                <Crown size={20} />
                <span className="font-bold text-sm">PROFESSIONAL APPROACH</span>
              </div>
            </div>
            
            <div className="mt-8 space-y-8">
              {/* Header */}
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Trophy className="text-white" size={32} />
                </div>
                <h3 className={cn(
                  "font-bold text-[#003a70] mb-2",
                  isMobile ? "text-2xl" : "text-3xl"
                )}>
                  How Experts Position Themselves
                </h3>
                <p className="text-[#003a70]/80 font-medium">
                  Premium, polished, professional
                </p>
              </div>
              
              {/* Traits List */}
              <div className="space-y-4">
                {professionalTraits.map((trait, index) => (
                  <div 
                    key={index} 
                    className="bg-gradient-to-r from-white to-[#FEE6D8]/20 rounded-xl p-6 border border-[#FEE6D8]/30"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center flex-shrink-0">
                        <trait.icon className="text-white" size={16} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-[#003a70] mb-2 text-lg">
                          {trait.title}
                        </h4>
                        <p className="text-[#003a70]/70 text-sm md:text-base mb-3">
                          {trait.description}
                        </p>
                        <div className="bg-gradient-to-r from-[#FEE6D8]/30 to-[#FEE6D8]/20 rounded-lg p-3 border-l-4 border-[#FEE6D8]">
                          <p className="text-[#003a70] font-medium text-sm">
                            Result: {trait.impact}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Positioning Benefits */}
        <div className="max-w-6xl mx-auto animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <div className="bg-gradient-to-r from-[#003a70] to-[#003a70]/90 rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#FEE6D8]/5 via-transparent to-[#FEE6D8]/10 pointer-events-none"></div>
            
            <div className="relative z-10 space-y-8">
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="w-16 h-16 bg-[#FEE6D8]/20 rounded-full flex items-center justify-center">
                    <Star className="text-[#FEE6D8]" size={28} />
                  </div>
                </div>
                
                <h3 className={cn(
                  "font-bold text-white leading-tight",
                  isMobile ? "text-xl md:text-2xl" : "text-2xl lg:text-3xl"
                )}>
                  What Professional Positioning Gets You
                </h3>
                
                <p className={cn(
                  "text-white/90 leading-relaxed max-w-2xl mx-auto",
                  isMobile ? "text-base" : "text-lg"
                )}>
                  When you look professional, everything changes in your favor
                </p>
              </div>
              
              {/* Benefits Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
                {positioningBenefits.map((benefit, index) => (
                  <div 
                    key={index}
                    className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#FEE6D8]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <benefit.icon className="text-[#FEE6D8]" size={24} />
                      </div>
                      <div>
                        <h4 className="font-bold text-white mb-2 text-lg">
                          {benefit.title}
                        </h4>
                        <p className="text-white/80 text-sm md:text-base">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Bottom Statement */}
              <div className="text-center mt-10 pt-8 border-t border-white/20">
                <p className={cn(
                  "text-[#FEE6D8] font-bold leading-relaxed",
                  isMobile ? "text-lg" : "text-xl lg:text-2xl"
                )}>
                  The difference between amateur and expert 
                  <br />
                  is often just the quality of your materials
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Final Hook */}
        <div className="max-w-5xl mx-auto animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <div className="bg-gradient-to-br from-[#FEE6D8]/20 via-white to-[#FEE6D8]/10 rounded-3xl p-8 md:p-12 border-2 border-[#FEE6D8]/30 shadow-xl">
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center mx-auto">
                <Eye className="text-white" size={28} />
              </div>
              
              <h3 className={cn(
                "font-bold text-[#003a70] leading-tight",
                isMobile ? "text-xl md:text-2xl" : "text-2xl lg:text-3xl"
              )}>
                Your Clients Judge You in the First 7 Seconds
              </h3>
              
              <p className={cn(
                "text-[#003a70]/80 leading-relaxed",
                isMobile ? "text-base md:text-lg" : "text-lg lg:text-xl"
              )}>
                Make sure those 7 seconds position you as the premium choice, 
                not just another option.
              </p>
              
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-[#FEE6D8]/40 shadow-inner">
                <p className="text-[#003a70] font-bold text-lg md:text-xl">
                  Our Asset Pack ensures you always make the right first impression.
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </SectionLayout>
  );
};

export default ProfessionalPositioningSection;
